import type { ReactNode } from 'react';
import { PRODUCT_NAME } from '@os/shared';
import { useTranslation } from 'react-i18next';
import { DisplayTitle, Logo } from '../../ui';
import { CLIENT_BUILD, formatVersion } from '../about/version';

/**
 * Sign-in, sign-up, reset and guest entry (P15): the logo and a Fraunces hero with one word in italics beside (below,
 * on a phone) a floating card holding the form; the build line stays under the card.
 */
export function AuthLayout({ title, hero, children }: { title: string; hero?: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg px-4 py-10 md:flex-row md:gap-16 md:px-12">
      <div className="flex w-full max-w-sm flex-col gap-4 md:max-w-md">
        <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-widest text-fg-muted">
          <Logo size={32} decorative className="text-fg" />
          {PRODUCT_NAME}
        </p>
        <div data-hero="">
          <DisplayTitle as="p" size="md" className="md:text-hero" text={hero ?? t('auth.heroTitle')} />
        </div>
      </div>
      <div className="flex w-full max-w-sm flex-col items-center gap-4">
        <div data-auth-card="" className="flex w-full flex-col gap-6 rounded-3xl bg-chrome p-6 shadow-float sm:p-8">
          <h1 className="font-display text-display-md font-normal tracking-display text-fg">{title}</h1>
          {children}
        </div>
        {/* The build, discreet (P5g, backlog §16.7): a bug report from the login page names it too. */}
        <p data-version="" className="font-mono text-xs text-fg-muted">
          {formatVersion(CLIENT_BUILD, i18n.language)}
        </p>
      </div>
    </main>
  );
}

export function FormError({ message }: { message: string | null }) {
  return message ? (
    <p role="alert" className="font-sans text-sm text-danger">
      {message}
    </p>
  ) : null;
}
